import { cn } from '../../lib/cn'
import type { CourseBookEntry } from '../../hooks/useCourseBook'

interface CourseCardProps {
  course: CourseBookEntry
  /** Whether this is the course whose card is showing below. */
  selected: boolean
  onSelect: (key: string) => void
}

/**
 * One course in the club's book (the-grounds card §II).
 *
 * NO RECORD ROW. The design card credits each course's record to a member, and
 * the three fictional courses that used to live here shipped it as "Unclaimed"
 * ×3 instead. Neither is available now that the courses are real: nothing
 * publishes a course record, so the honest card does not have the row at all —
 * an "Unclaimed" beside fifteen real municipal courses would be a claim about
 * fifteen real places rather than an empty state.
 *
 * `par · yardage` appear only when the server publishes them, which it does
 * ALL OR NOTHING — a partial sum over a half-seeded card is a wrong number,
 * not a rough one. A course the club has not carded says so.
 *
 * "Drawn" means the course has PUBLISHED geometry (the fact behind the Caddie
 * map), not that somebody has a draft open.
 */
export function CourseCard({ course, selected, onSelect }: CourseCardProps) {
  const carded = course.par !== null

  return (
    <li
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'flex min-h-[210px] flex-col rounded-club-lg border p-7',
        selected ? 'border-fairway bg-sage-soft/40' : 'border-ink/12 bg-parchment',
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <p className="font-display text-[13px] text-brass-deep">{course.operator}</p>
        {course.mapped && (
          <span className="whitespace-nowrap rounded-club-sm border border-brass/55 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-brass-deep">
            Drawn
          </span>
        )}
      </div>

      <h3 className="mb-2 mt-1.5 font-display text-[22px] font-medium text-pine">{course.name}</h3>
      <p className="flex-1 text-sm text-ink-soft">
        {course.city} · {course.holes_count} holes
      </p>

      <dl className="mt-3.5">
        {[
          { label: 'Par', value: carded ? String(course.par) : 'Not yet carded' },
          {
            label: 'Length',
            value:
              course.total_yardage === null
                ? '—'
                : `${course.total_yardage.toLocaleString('en-US')} yds`,
          },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between border-t border-brass/28 py-2"
          >
            <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
              {row.label}
            </dt>
            <dd className="font-display text-sm tabular-nums text-pine">{row.value}</dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={() => onSelect(course.key)}
        // The visible words are the same on every card, so the accessible name
        // carries the course. It CONTAINS the visible label (WCAG 2.5.3) rather
        // than replacing it.
        aria-label={`See the card for ${course.name}`}
        className="mt-3.5 self-start rounded-club-sm border border-ink-soft/35 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-brass-deep"
      >
        See the card
      </button>
    </li>
  )
}
